import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import FormStepperCallback from '@/components/Form/FormStepperCallback.vue';
import LPFormOffer from '@/components/ListingPage/Form/LPFormOffer.vue';
import { useApiListing } from '@/composables/api/listing';
import tFormStepsFactory from '@/i18n/translations/components/formStepsFactory.json';
import type { FactoryId, FNextOffer, FormStepsFactory, Step } from '@/types/formStepsFactory';

import { useStepsAuth } from './auth';

export const useStepsLpOffer = () => {
  const factoryId: FactoryId = 'lp-offer';

  const { storeOffer } = useApiListing();
  const { t } = useI18n({ ...tFormStepsFactory, useScope: 'global' });

  const getFormStepsLpOffer = (): FormStepsFactory => {
    const { getFormStepsAuth } = useStepsAuth();
    const authFormSteps = getFormStepsAuth();

    const submit = (formData: FNextOffer['formData']) => {
      const data = new FormData();

      Object.keys(formData).forEach(k => {
        const key = k as keyof typeof formData;
        const value = formData[key];

        switch (typeof value) {
          case 'boolean':
            data.append(key, value ? '1' : '0');
            break;
          case 'number':
            data.append(key, value.toString());
            break;
          default:
            if (value) data.append(key, value.toString());
            break;
        }
      });

      return storeOffer(data);
    };

    return computed<Step[]>(() => {
      const authFormStepsLocal = [...authFormSteps.value];

      authFormStepsLocal[0].subtitle = t('subtitle.offerLogin');
      authFormStepsLocal[0].title = t('title.offerLogin');

      return [
        {
          component: () => LPFormOffer,
          formId: 'lp-offer',
          subtitle: t('subtitle.offer'),
          title: t('title.offer'),
        },
        ...authFormStepsLocal,
        {
          component: () => FormStepperCallback,
          componentProps: {
            onMountedCallbackArgs: (next: FNextOffer) => [next.formData],
            onMountedCallbackFn: submit,
            formInfoMsgPropsError: {
              qIconProps: {
                color: 'negative',
                name: 'warning',
                size: '5rem',
              },
              subtitle: { text: t('subtitle.offerError') },
              title: { text: t('title.offerError') },
            },
            formInfoMsgPropsSuccess: {
              qIconProps: {
                color: 'primary',
                name: 'success',
                size: '5rem',
              },
              subtitle: { text: t('subtitle.offerSuccess') },
              title: { text: t('title.offerSuccess') },
            },
          },
          formId: 'callback',
        },
      ];
    });
  };

  return { factoryId, getFormStepsLpOffer };
};
